import { Disclosure } from "@headlessui/react";
import { BellIcon } from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { useAuth } from "../hooks/useAuth";
import { classNames } from "../utils/functions";
import Avatar from "../common/Avatar";

const navigation = [
  { name: "Home", href: "/home", current: true },
  { name: "Wardrobe", href: "/wardrobe", current: false },
  { name: "Lookbook", href: "/lookbook", current: false },
  { name: "Styling", href: "/styling", current: false },
];

const userNavigation = [
  { name: "Profile", href: "profile" },
  { name: "Settings", href: "settings" },
];

export default function MobileMenu() {
  const user = useSelector((state: RootState) => state.user);
  const { logout } = useAuth();

  return (
    <Disclosure.Panel className="sm:hidden relative">
      {/* Luxury background with glass-morphism */}
      <div className="absolute inset-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md" />
      <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 via-purple-500/5 to-indigo-500/5" />

      {/* Navigation items */}
      <div className="relative space-y-2 px-6 py-4">
        {/* Decorative top gradient line */}
        <div className="h-px bg-gradient-to-r from-transparent via-pink-500/30 to-transparent mb-4" />

        {navigation.map((item) => (
          <Disclosure.Button
            key={item.name}
            as="a"
            href={item.href}
            className={classNames(
              item.current
                ? "bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-indigo-500/10 text-gray-900 dark:text-white border-l-pink-500"
                : "text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-800 dark:hover:to-gray-700 hover:text-gray-900 dark:hover:text-white border-l-transparent hover:border-l-purple-500",
              "group relative flex items-center border-l-4 py-3 pl-6 pr-4 text-sm font-light tracking-wider uppercase transition-all duration-300 rounded-r-lg"
            )}
            aria-current={item.current ? "page" : undefined}
          >
            {/* Decorative gradient accent */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 group-hover:h-6 bg-gradient-to-b from-pink-500 via-purple-500 to-indigo-500 rounded-full transition-all duration-300" />

            <span className="relative z-10 ml-2">{item.name}</span>

            {/* Arrow indicator */}
            <div className="ml-auto w-3 h-3 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </Disclosure.Button>
        ))}
      </div>

      {/* User section with luxury styling */}
      <div className="relative border-t border-gray-200/50 dark:border-gray-700/50 px-6 py-4">
        <div className="flex items-center mb-4">
          {/* Enhanced avatar with gradient border */}
          <div className="flex-shrink-0 relative">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 p-0.5">
              <div className="w-full h-full rounded-full bg-white dark:bg-gray-900 p-0.5">
                <Avatar avatarUrl={user.avatar} name={user.name} size={8} />
              </div>
            </div>
          </div>

          {/* User info with elegant typography */}
          <div className="ml-4 flex-1">
            <div className="text-sm font-light tracking-wider uppercase text-gray-900 dark:text-gray-100">
              {user?.name}
            </div>
            <div className="text-xs font-light tracking-wide text-gray-500 dark:text-gray-400">
              {user.email}
            </div>
          </div>

          {/* Elegant notification button */}
          <button
            type="button"
            className="group relative ml-auto flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:scale-110 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            <span className="absolute -inset-1.5" />
            <span className="sr-only">View notifications</span>
            <BellIcon
              className="h-5 w-5 group-hover:rotate-12 transition-transform duration-300"
              aria-hidden="true"
            />

            {/* Subtle glow effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-500/10 via-purple-500/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
        </div>

        {/* User navigation with luxury styling */}
        <div className="space-y-2">
          {userNavigation.map((item) => (
            <Disclosure.Button
              key={item.name}
              as="a"
              href={item.href}
              className="group relative flex items-center w-full px-4 py-2 text-sm font-light tracking-wider uppercase text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-800 dark:hover:to-gray-700 rounded-lg transition-all duration-300"
            >
              {/* Decorative gradient line */}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 group-hover:h-4 bg-gradient-to-b from-pink-500 via-purple-500 to-indigo-500 rounded-full transition-all duration-300" />

              <span className="relative z-10 ml-2">{item.name}</span>

              {/* Arrow indicator */}
              <div className="ml-auto w-3 h-3 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </Disclosure.Button>
          ))}

          {/* Logout button with elegant styling */}
          <button
            onClick={logout}
            className="group relative flex items-center w-full px-4 py-2 text-sm font-light tracking-wider uppercase text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 dark:hover:from-red-900/20 dark:hover:to-red-800/20 rounded-lg transition-all duration-300"
          >
            {/* Decorative gradient line */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 group-hover:h-4 bg-gradient-to-b from-red-500 to-red-600 rounded-full transition-all duration-300" />

            <span className="relative z-10 ml-2">Sign Out</span>

            {/* Arrow indicator */}
            <div className="ml-auto w-3 h-3 text-red-400 dark:text-red-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                />
              </svg>
            </div>
          </button>
        </div>

        {/* Decorative bottom gradient line */}
        <div className="h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent mt-4" />
      </div>
    </Disclosure.Panel>
  );
}
